import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField, Avatar, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, CircularProgress,
  InputAdornment, IconButton, Alert, Divider, Rating, Tooltip
} from '@mui/material';
import { Add, Search, Edit, CalendarMonth, Star, Close, PersonAdd } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const specColors = ['#dbeafe', '#dcfce7', '#f3e8ff', '#fef9c3', '#fee2e2', '#cffafe', '#ffedd5'];
const specTextColors = ['#1d4ed8', '#15803d', '#7c3aed', '#a16207', '#b91c1c', '#0e7490', '#c2410c'];

const defaultForm = {
  name: '', email: '', phone: '', password: '',
  specialization: '', subSpecialization: '', department: '',
  registrationNumber: '', consultationFee: 0, followUpFee: 0, emergencyFee: 0,
  totalExperienceYears: 0, bio: '', languages: ''
};

const Doctors = () => {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [qualifications, setQualifications] = useState([{ degree: '', institution: '', year: '' }]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [drRes, deptRes] = await Promise.all([
        api.get('/doctors', { params: { search, limit: 50 } }),
        api.get('/departments')
      ]);
      setDoctors(drRes.data.data || []);
      setDepartments(deptRes.data.data || []);
    } catch { toast.error('Failed to load doctors'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.specialization) {
      setError('Name, email and specialization are required');
      return;
    }
    setError(''); setSubmitting(true);
    try {
      await api.post('/doctors', {
        ...form,
        qualifications: qualifications.filter(q => q.degree),
        languages: form.languages ? form.languages.split(',').map((s: string) => s.trim()) : []
      });
      toast.success('Doctor registered successfully');
      setOpen(false);
      setForm(defaultForm);
      setQualifications([{ degree: '', institution: '', year: '' }]);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to register doctor');
    } finally { setSubmitting(false); }
  };

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const setQual = (i: number, k: string, v: string) =>
    setQualifications(q => q.map((item, idx) => idx === i ? { ...item, [k]: v } : item));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>Doctors</Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.3 }}>{doctors.length} registered doctors</Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAdd />} onClick={() => { setOpen(true); setError(''); setForm(defaultForm); }}
          sx={{ borderRadius: 2, bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' }, textTransform: 'none', fontWeight: 600, px: 2.5 }}>
          Register Doctor
        </Button>
      </Box>

      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, p: 2, mb: 2 }}>
        <TextField
          placeholder="Search by name or specialization..."
          value={search} onChange={e => setSearch(e.target.value)} fullWidth size="small"
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94a3b8', fontSize: 20 }} /></InputAdornment> }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
        />
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#3b82f6' }} /></Box>
      ) : (
        <Grid container spacing={2.5}>
          {doctors.map((doc, idx) => (
            <Grid item xs={12} sm={6} lg={4} key={doc._id}>
              <Card elevation={0} onClick={() => navigate(`/doctors/${doc._id}`)}
                sx={{ border: '1px solid #e2e8f0', borderRadius: 3, cursor: 'pointer', '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.1)', transform: 'translateY(-2px)', borderColor: '#bfdbfe' }, transition: 'all 0.2s' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Avatar sx={{ width: 56, height: 56, bgcolor: specColors[idx % specColors.length], color: specTextColors[idx % specTextColors.length], fontSize: 20, fontWeight: 800 }}>
                      {doc.user?.name?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }} noWrap>
                        Dr. {doc.user?.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#3b82f6', fontWeight: 600 }}>{doc.specialization}</Typography>
                      {doc.department && (
                        <Typography variant="caption" sx={{ color: '#64748b' }}>{doc.department.name}</Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {doc.totalExperienceYears > 0 && (
                      <Chip label={`${doc.totalExperienceYears} yrs exp`} size="small" sx={{ bgcolor: '#f1f5f9', fontSize: 11 }} />
                    )}
                    {doc.qualifications?.length > 0 && (
                      <Chip label={doc.qualifications[0].degree} size="small" sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontSize: 11 }} />
                    )}
                    {doc.acceptingNewPatients && (
                      <Chip label="Available" size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontSize: 11 }} />
                    )}
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={1}>
                    {[
                      { label: 'Consultation', value: `₹${doc.consultationFee || 0}` },
                      { label: 'Follow-up', value: `₹${doc.followUpFee || 0}` },
                    ].map(({ label, value }) => (
                      <Grid item xs={6} key={label}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>{label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>{value}</Typography>
                      </Grid>
                    ))}
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button size="small" variant="outlined" startIcon={<CalendarMonth />}
                      onClick={e => { e.stopPropagation(); navigate(`/doctors/${doc._id}?tab=schedule`); }}
                      sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontSize: 12, borderColor: '#e2e8f0' }}>
                      Schedule
                    </Button>
                    <Button size="small" variant="contained"
                      onClick={e => { e.stopPropagation(); navigate(`/appointments?doctor=${doc._id}`); }}
                      sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontSize: 12, bgcolor: '#1e40af' }}>
                      Book
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {doctors.length === 0 && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography sx={{ color: '#94a3b8', fontSize: 16 }}>No doctors registered yet</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      {/* Register Doctor Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, color: '#0f172a', borderBottom: '1px solid #f1f5f9' }}>
          Register New Doctor
          <IconButton size="small" onClick={() => setOpen(false)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" sx={{ color: '#1e40af', fontWeight: 700, borderBottom: '2px solid #dbeafe', pb: 0.5, mb: 1 }}>Personal Information</Typography>
            </Box>
            {[
              { label: 'Full Name *', k: 'name', col: 1 },
              { label: 'Email *', k: 'email', col: 1 },
              { label: 'Phone', k: 'phone', col: 1 },
              { label: 'Password', k: 'password', col: 1, type: 'password' },
            ].map(({ label, k, type }) => (
              <Box key={k}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>{label}</Typography>
                <TextField fullWidth size="small" type={type || 'text'} value={form[k]} onChange={e => set(k, e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Box>
            ))}

            <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
              <Typography variant="subtitle2" sx={{ color: '#1e40af', fontWeight: 700, borderBottom: '2px solid #dbeafe', pb: 0.5, mb: 1 }}>Medical Details</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Specialization *</Typography>
              <TextField fullWidth size="small" value={form.specialization} onChange={e => set('specialization', e.target.value)}
                placeholder="e.g. Cardiology, Orthopedics" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Sub-Specialization</Typography>
              <TextField fullWidth size="small" value={form.subSpecialization} onChange={e => set('subSpecialization', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Department</Typography>
              <TextField select fullWidth size="small" value={form.department} onChange={e => set('department', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                <MenuItem value="">None</MenuItem>
                {departments.map(d => <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>)}
              </TextField>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Registration Number</Typography>
              <TextField fullWidth size="small" value={form.registrationNumber} onChange={e => set('registrationNumber', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Years of Experience</Typography>
              <TextField fullWidth size="small" type="number" value={form.totalExperienceYears} onChange={e => set('totalExperienceYears', Number(e.target.value))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Languages (comma separated)</Typography>
              <TextField fullWidth size="small" value={form.languages} onChange={e => set('languages', e.target.value)}
                placeholder="e.g. English, Hindi, Tamil" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>

            <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
              <Typography variant="subtitle2" sx={{ color: '#1e40af', fontWeight: 700, borderBottom: '2px solid #dbeafe', pb: 0.5, mb: 1 }}>Consultation Fees (₹)</Typography>
            </Box>
            {[
              { label: 'Consultation', k: 'consultationFee' },
              { label: 'Follow-up', k: 'followUpFee' },
              { label: 'Emergency', k: 'emergencyFee' },
              { label: 'Online', k: 'onlineConsultationFee' },
            ].map(({ label, k }) => (
              <Box key={k}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>{label} Fee</Typography>
                <TextField fullWidth size="small" type="number" value={form[k] || 0} onChange={e => set(k, Number(e.target.value))}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Box>
            ))}

            <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
              <Typography variant="subtitle2" sx={{ color: '#1e40af', fontWeight: 700, borderBottom: '2px solid #dbeafe', pb: 0.5, mb: 1 }}>Qualifications</Typography>
              {qualifications.map((q, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr auto', gap: 1, mb: 1 }}>
                  <TextField size="small" placeholder="Degree (e.g. MBBS)" value={q.degree} onChange={e => setQual(i, 'degree', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  <TextField size="small" placeholder="Institution" value={q.institution} onChange={e => setQual(i, 'institution', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  <TextField size="small" placeholder="Year" type="number" value={q.year} onChange={e => setQual(i, 'year', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  <IconButton size="small" onClick={() => setQualifications(q => q.filter((_, idx) => idx !== i))} disabled={qualifications.length === 1} sx={{ color: '#ef4444' }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" onClick={() => setQualifications(q => [...q, { degree: '', institution: '', year: '' }])}
                sx={{ textTransform: 'none', color: '#3b82f6', fontSize: 12 }}>+ Add Qualification</Button>
            </Box>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Bio / About</Typography>
              <TextField fullWidth size="small" multiline rows={3} value={form.bio} onChange={e => set('bio', e.target.value)}
                placeholder="Brief professional summary..." sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f1f5f9' }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' }, px: 3 }}>
            {submitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Register Doctor'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Doctors;
